import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BsDatabaseFill } from "react-icons/bs";
import { CircularProgress } from "@nextui-org/react";
import { subMonths, subDays, differenceInCalendarDays } from "date-fns";
import {
  MdOutlineStarBorder,
  MdOutlineArrowOutward,
  MdOutlinePhoneEnabled,
  MdOutlinePhoneMissed,
  MdOutlineWarningAmber,
} from "react-icons/md";

import LineChart from "../../components/charts/LineChart";
import CircularGauge from "../calls/CallModal/CircularGauge";

import { useAppContext } from "../../contexts/AppContext";
import { getGradeBackgroundColor } from "../../utils/colorUtils";
import myAxios from "../../utils/axiosWrapper";
import getConfig from "../../conf/config";

// Same semantic palette as the grade pills / overview cards (tailwind `grade.*`).
const QUALITY_SEGMENTS = [
  { key: "green_calls", label: "Green Calls", color: "#05CD99", filter: "green" },
  { key: "orange_calls", label: "Orange Calls", color: "#FBBC05", filter: "orange" },
  { key: "red_calls", label: "Red Calls", color: "#EF4040", filter: "red" },
];

// Colour lives on the icon chip; the figure itself stays in ink so six cards
// side by side don't read as a wall of coloured numbers.
const KPI_CARDS = [
  {
    key: "total_calls",
    label: "Number of Calls",
    icon: MdOutlinePhoneEnabled,
    chipClassName: "bg-primary/10 text-primary",
    filter: null,
  },
  {
    key: "green_calls",
    label: "Green Calls",
    icon: MdOutlinePhoneEnabled,
    chipClassName: "bg-grade-high/10 text-grade-high",
    filter: "green",
  },
  {
    key: "orange_calls",
    label: "Orange Calls",
    icon: MdOutlinePhoneEnabled,
    chipClassName: "bg-grade-medium/10 text-grade-medium",
    filter: "orange",
  },
  {
    key: "red_calls",
    label: "Red Calls",
    icon: MdOutlinePhoneEnabled,
    chipClassName: "bg-grade-low/10 text-grade-low",
    filter: "red",
  },
  {
    key: "frustrated_clients",
    label: "Frustrated Clients",
    icon: MdOutlineWarningAmber,
    chipClassName: "bg-grade-low/10 text-grade-low",
    filter: "frustrated",
  },
  {
    key: "answerphone",
    label: "Answering Machine",
    icon: MdOutlinePhoneMissed,
    chipClassName: "bg-gray-100 text-gray-500",
    filter: "answerphone",
  },
];

const RepresentativeModale = ({ operatorId, handleOnClose = () => {} }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { callsDateRange, setSearchText, setPreventSearchReset } = useAppContext();

  const [operatorGlobalData, setOperatorGlobalData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [currentDateRange, setCurrentDateRange] = useState(callsDateRange);

  const onFetchOperatorGlobalData = async () => {
    setIsLoading(true);
    const formattedStartDate = new Date(currentDateRange.startDate).toISOString();
    const formattedEndDate = new Date(currentDateRange.endDate).toISOString();

    const response = await myAxios.get(
      `${
        getConfig().API_URL
      }/audio/api/v1/operators/${operatorId}/details?start_date=${formattedStartDate}&end_date=${formattedEndDate}`
    );

    const retrievedData = response?.data?.[0];
    if (retrievedData) {
      setOperatorGlobalData(retrievedData);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    onFetchOperatorGlobalData();
  }, [currentDateRange]);

  const operatorCallGrades = operatorGlobalData?.grades?.grades;
  const operatorCalls = operatorGlobalData?.calls || {};

  const avgGrade = operatorGlobalData?.grades?.avg_grade;
  const hasGrade = typeof avgGrade === "number" && !Number.isNaN(avgGrade);
  const roundedGrade = hasGrade ? Math.round(avgGrade * 10) / 10 : null;

  const totalCalls = operatorCalls.total_calls || 0;
  const qualityCounts = QUALITY_SEGMENTS.map((segment) => operatorCalls[segment.key] || 0);
  const qualityTotal = qualityCounts.reduce((sum, count) => sum + count, 0);

  const operatorName = `${operatorGlobalData?.first_name || ""} ${
    operatorGlobalData?.last_name || ""
  }`.trim();
  const initials = `${operatorGlobalData?.first_name?.[0] || ""}${
    operatorGlobalData?.last_name?.[0] || ""
  }`.toUpperCase();
  const subtitleParts = [operatorGlobalData?.team_name, operatorGlobalData?.department_name].filter(
    Boolean
  );

  const calendarDefinedRanges = [
    {
      startDate: subDays(new Date(), 7),
      endDate: new Date(),
      label: t("Last 7 days"),
    },
    {
      startDate: subMonths(new Date(), 3),
      endDate: new Date(),
      label: t("Last 3 months"),
    },
    {
      startDate: subMonths(new Date(), 6),
      endDate: new Date(),
      label: t("Last 6 months"),
    },
  ];

  // The initial range comes from the global calls filter, so a segment is "active"
  // when its span matches the current one — not only after the user clicked it.
  const currentRangeDays = differenceInCalendarDays(
    new Date(currentDateRange.endDate),
    new Date(currentDateRange.startDate)
  );
  const isRangeActive = (definedRange) =>
    Math.abs(
      differenceInCalendarDays(definedRange.endDate, definedRange.startDate) - currentRangeDays
    ) <= 2;

  const onRedirectToCalls = (filter) => {
    setPreventSearchReset(true);
    setSearchText(operatorGlobalData?.operator_name || operatorName || "");
    handleOnClose();
    navigate(filter ? `/calls?filter=${filter}` : "/calls");
  };

  const showInitialLoader = isLoading && !operatorGlobalData;

  const renderChartEmptyState = () => (
    <div className="w-full min-h-[180px] flex flex-col items-center justify-center">
      <BsDatabaseFill className="text-gray-300 w-10 h-10" />
      <h3 className="text-sm text-gray-400 mt-3">{t("No records to display")}</h3>
    </div>
  );

  return (
    <div className="flex flex-col w-full bg-background px-6 pt-5 pb-5 max-h-[85vh] overflow-y-auto">
      {showInitialLoader && (
        <div className="w-full min-h-[420px] flex items-center justify-center">
          <CircularProgress
            aria-label="Loading..."
            color="primary"
            label={t("Loading...")}
          />
        </div>
      )}
      {!showInitialLoader && (
        <div
          className={`flex flex-col space-y-3 transition-opacity duration-200 ${
            isLoading ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          {/* Header: identity + period selector */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex shrink-0 items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary text-sm font-semibold">
                {initials}
              </div>
              <div className="flex flex-col min-w-0">
                <h1 className="text-xl leading-tight text-[#171717] font-semibold capitalize truncate">
                  {operatorName}
                </h1>
                {subtitleParts.length > 0 && (
                  <span className="text-xs text-gray-500 truncate">
                    {subtitleParts.join(" • ")}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0 bg-white border-1 border-[#E5E5E5] border-solid rounded-lg p-1 shadow-custom-light">
              {calendarDefinedRanges.map((definedRange) => (
                <button
                  key={definedRange.label}
                  type="button"
                  onClick={() => {
                    setCurrentDateRange({
                      startDate: definedRange.startDate,
                      endDate: definedRange.endDate,
                    });
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                    isRangeActive(definedRange)
                      ? "bg-primary text-white"
                      : "text-[#404040] hover:bg-gray-100"
                  }`}
                >
                  {definedRange.label}
                </button>
              ))}
            </div>
          </div>

          {/* Average grade + quality distribution */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1 bg-white rounded-xl shadow-custom-light p-4 flex flex-col items-center justify-center text-center">
              {hasGrade ? (
                <CircularGauge
                  grade={roundedGrade}
                  size={64}
                />
              ) : (
                <span className="text-3xl font-semibold text-gray-300">–</span>
              )}
              <span className="mt-2 text-sm font-semibold text-[#171717]">{t("Note moyenne")}</span>
              <span className="text-xs text-gray-400">{t("sur la période sélectionnée")}</span>
            </div>
            <div className="col-span-2 bg-white rounded-xl shadow-custom-light p-4 flex flex-col justify-center">
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="text-sm font-semibold text-[#171717]">
                  {t("Répartition des appels évalués")}
                </h3>
                <span className="text-xs text-gray-400 tabular-nums">
                  {qualityTotal} / {totalCalls} {t("appels")}
                </span>
              </div>
              {qualityTotal > 0 ? (
                <>
                  <div className="flex h-3 w-full gap-[2px] rounded-full overflow-hidden">
                    {QUALITY_SEGMENTS.map((segment, index) =>
                      qualityCounts[index] > 0 ? (
                        <div
                          key={segment.key}
                          title={`${t(segment.label)} — ${qualityCounts[index]}`}
                          className="h-full min-w-[6px] transition-all duration-500"
                          style={{
                            width: `${(qualityCounts[index] / qualityTotal) * 100}%`,
                            backgroundColor: segment.color,
                          }}
                        />
                      ) : null
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3">
                    {QUALITY_SEGMENTS.map((segment, index) => (
                      <button
                        key={segment.key}
                        type="button"
                        onClick={() => onRedirectToCalls(segment.filter)}
                        className="group flex items-center gap-2"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: segment.color }}
                        />
                        <span className="text-xs text-[#737373] group-hover:text-[#171717] transition-colors">
                          {t(segment.label)}
                        </span>
                        <span className="text-xs font-semibold text-[#171717] tabular-nums">
                          {Math.round((qualityCounts[index] / qualityTotal) * 100)}%
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <span className="text-sm text-gray-400">
                  {t("Aucun appel évalué sur la période")}
                </span>
              )}
            </div>
          </div>

          {/* KPI cards — every card opens the calls list pre-filtered on this rep */}
          <div className="grid grid-cols-6 gap-3">
            {KPI_CARDS.map((kpi) => (
              <button
                key={kpi.key}
                type="button"
                onClick={() => onRedirectToCalls(kpi.filter)}
                title={t(kpi.label)}
                className="group bg-white rounded-xl shadow-custom-light p-3 flex flex-col items-start text-left min-w-0 transition-shadow hover:shadow-md"
              >
                <div className="flex items-center justify-between w-full">
                  <div
                    className={`flex items-center justify-center w-7 h-7 rounded-lg ${kpi.chipClassName}`}
                  >
                    <kpi.icon size={15} />
                  </div>
                  <MdOutlineArrowOutward
                    size={13}
                    className="text-primary opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </div>
                <span className="mt-2 text-[18px] leading-6 font-semibold text-[#171717] tabular-nums">
                  {(operatorCalls[kpi.key] || 0).toLocaleString("fr-FR")}
                </span>
                <span className="text-[11px] leading-4 text-[#737373] mt-0.5 truncate w-full">
                  {t(kpi.label)}
                </span>
              </button>
            ))}
          </div>

          {/* Grade trend */}
          <div className="bg-white rounded-xl shadow-custom-light px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-[#171717]">
                  {t("Évolution de la note")}
                </h3>
                <span className="text-xs text-gray-400">{t("Note moyenne par jour")}</span>
              </div>
              {hasGrade && (
                <div
                  className={`flex items-center font-semibold px-[8px] py-[4px] rounded-md text-xs ${getGradeBackgroundColor(
                    roundedGrade
                  )}`}
                >
                  <MdOutlineStarBorder size={16} />
                  <span className="mx-[4px] tabular-nums">{roundedGrade}</span>
                </div>
              )}
            </div>
            {operatorCallGrades && Object.keys(operatorCallGrades).length > 0 ? (
              <LineChart
                data={operatorCallGrades}
                type="calls"
                title="Grades"
                minMaxAxisDiffFactor={0.1}
                minHeight={180}
              />
            ) : (
              renderChartEmptyState()
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RepresentativeModale;
